import { Injectable } from '@angular/core';

/**
 * This service is used to verify the validity of tax numbers.
 * The corresponding logic is taken from https://atc.bmwgroup.net/confluence/display/FSMCIM/Current+requirements+-Validation+of+customer+address+data+-+OVERVIEW#CurrentrequirementsValidationofcustomeraddressdataOVERVIEW-#6aNationalID
 */
@Injectable({
  providedIn: 'root',
})
export class TaxNumberValidationService {
  /**
   * Validates a taxNumber for a provided country. Spaces are removed
   * @returns true if tax number is valid in country
   */
  isTaxNumberValid(taxNumber: string | undefined, taxNumberCountry: string | undefined): boolean {
    if (!taxNumberCountry || !taxNumber) {
      return true;
    }

    // Remove all spaces
    const taxNumberWOSpaces = taxNumber.replace(/\s+/g, '');

    switch (taxNumberCountry) {
      case 'IT':
        return this.validateTaxNumberItalian(taxNumberWOSpaces);

      case 'ES':
        return this.validateTaxNumberSpanish(taxNumberWOSpaces);

      case 'PT':
        return this.validateTaxNumberPortuguese(taxNumberWOSpaces);

      default:
        // All remaining countries do not require a tax number validation
        return true;
    }
  }

  private validateTaxNumberItalian(taxNumber: string) {
    const taxNumberRegex: RegExp = /^[A-Z]{6}\d{2}[A-Z]\d{2}[A-Z]\d{3}[A-Z]$/i;
    const isRegexMatching = taxNumberRegex.test(taxNumber);

    return isRegexMatching && this.isChecksumValidItalian(taxNumber);
  }

  /**
   * Validates the checksum character of an Italian Tax Code.
   *
   * @param taxNumber - A string representing the Italian Tax Code to validate. It must be exactly 16 characters long.
   * @returns A boolean indicating whether the checksum character is valid (`true`) or not (`false`).
   *
   * @description
   * This function calculates the checksum character of an Italian Tax Code and compares it with the
   * provided checksum character (the 16th character of the Tax Code). The validation follows the algorithm specified
   * by the Italian government:
   *
   * 1. **Input Validation:**
   *    - Ensure the Tax Code is exactly 16 characters long.
   *
   * 2. **Character Position Mapping:**
   *    - For each of the first 15 characters (positions 1 to 15):
   *      - **Odd Positions (1-based indexing):**
   *        - Use the `oddMapping` to get the corresponding numeric value of the character.
   *      - **Even Positions:**
   *        - Use the `evenMapping` to get the corresponding numeric value of the character.
   *    - Sum all the numeric values obtained from the mappings.
   *
   *    **Mappings Explanation:**
   *    - The `oddMapping` and `evenMapping` are predefined objects that map each character (`A-Z` and `0-9`) to a specific numeric value.
   *    - These mappings are based on the Italian Tax Code checksum calculation rules.
   *
   * 3. **Checksum Character Calculation:**
   *    - Calculate the remainder of the sum when divided by 26 (`remainder = sum % 26`).
   *    - Determine the expected checksum character by mapping the remainder to a letter:
   *      - Use the English alphabet where `A` corresponds to `0`, `B` to `1`, ..., `Z` to `25`.
   *      - For example, if the remainder is `0`, the expected checksum character is `'A'`.
   *
   * 4. **Validation:**
   *    - Compare the calculated expected checksum character with the actual checksum character (the 16th character of the Tax Code).
   *    - If they match, the Tax Code has a valid checksum and the function returns `true`.
   *    - If they do not match, the Tax Code is invalid and the function returns `false`.
   */
  private isChecksumValidItalian(taxNumber: string): boolean {
    if (taxNumber.length !== 16) {
      return false;
    }

    // Required for mapping of characters
    const upperCaseTaxNumber = taxNumber.toUpperCase();

    const oddMapping: { [key: string]: number } = {
      '0': 1,
      '1': 0,
      '2': 5,
      '3': 7,
      '4': 9,
      '5': 13,
      '6': 15,
      '7': 17,
      '8': 19,
      '9': 21,
      A: 1,
      B: 0,
      C: 5,
      D: 7,
      E: 9,
      F: 13,
      G: 15,
      H: 17,
      I: 19,
      J: 21,
      K: 2,
      L: 4,
      M: 18,
      N: 20,
      O: 11,
      P: 3,
      Q: 6,
      R: 8,
      S: 12,
      T: 14,
      U: 16,
      V: 10,
      W: 22,
      X: 25,
      Y: 24,
      Z: 23,
    };

    const evenMapping: { [key: string]: number } = {
      '0': 0,
      '1': 1,
      '2': 2,
      '3': 3,
      '4': 4,
      '5': 5,
      '6': 6,
      '7': 7,
      '8': 8,
      '9': 9,
      A: 0,
      B: 1,
      C: 2,
      D: 3,
      E: 4,
      F: 5,
      G: 6,
      H: 7,
      I: 8,
      J: 9,
      K: 10,
      L: 11,
      M: 12,
      N: 13,
      O: 14,
      P: 15,
      Q: 16,
      R: 17,
      S: 18,
      T: 19,
      U: 20,
      V: 21,
      W: 22,
      X: 23,
      Y: 24,
      Z: 25,
    };

    const checksumChars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

    let sum = 0;

    for (let i = 0; i < 15; i++) {
      const c = upperCaseTaxNumber.charAt(i);
      let value = 0;

      if (!/[A-Z0-9]/.test(c)) {
        // Invalid character found
        return false;
      }

      value = i % 2 ? evenMapping[c] : oddMapping[c];

      if (value === undefined) {
        // Character not found in mapping
        return false;
      }

      sum += value;
    }

    const remainder = sum % 26;
    const expectedChecksumChar = checksumChars.charAt(remainder);

    const actualChecksumChar = upperCaseTaxNumber.charAt(15);

    return expectedChecksumChar === actualChecksumChar;
  }

  private validateTaxNumberSpanish(taxNumber: string) {
    const taxNumberRegex: RegExp =
      /^(?:(?:[0-9]{8}|[MLKXYZ][0-9]{7,8})[A-HJ-NP-TV-Z]|[KPQS][0-9]{7}[A-J]|[ABEH][0-9]{7}[0-9]|[CDFGJNPRUVW][0-9]{7}[0-9A-J])$/;

    return taxNumberRegex.test(taxNumber) && this.isValidationCharacterValidSpanish(taxNumber);
  }

  /**
   * Validates a Spanish Tax Number (NIF or NIE).
   *
   * @param taxNumber - The Spanish Tax Number to validate. It can be in the format of NIF (for Spanish nationals)
   *                    or NIE (for foreigners with a residence permit).
   * @returns A boolean indicating whether the tax number is valid (`true`) or not (`false`).
   *
   * @description
   * This function validates Spanish Tax Numbers for individuals (both Spanish nationals and foreigners).
   *
   * **For Spanish nationals (NIF):**
   * - **Format:** Eight digits followed by a letter (e.g., `12345678Z`).
   * - **Zeros on the left are significant and cannot be removed.**
   * - **Validation Character Calculation:**
   *   1. Take the numeric part of the NIF (all eight digits).
   *   2. Calculate the remainder of dividing the numeric part by 23.
   *   3. Map the remainder to a letter using the following table:
   *
   *      | Remainder | Letter |
   *      |-----------|--------|
   *      | 0         | T      |
   *      | 1         | R      |
   *      | 2         | W      |
   *      | 3         | A      |
   *      | 4         | G      |
   *      | 5         | M      |
   *      | 6         | Y      |
   *      | 7         | F      |
   *      | 8         | P      |
   *      | 9         | D      |
   *      | 10        | X      |
   *      | 11        | B      |
   *      | 12        | N      |
   *      | 13        | J      |
   *      | 14        | Z      |
   *      | 15        | S      |
   *      | 16        | Q      |
   *      | 17        | V      |
   *      | 18        | H      |
   *      | 19        | L      |
   *      | 20        | C      |
   *      | 21        | K      |
   *      | 22        | E      |
   *
   *   4. The corresponding letter is the validation character. It should match the last character of the NIF.
   *
   * **For foreigners with a residence permit (NIE):**
   * - **Format:** A letter (`X`, `Y`, or `Z`), followed by seven digits, and a control character (letter).
   * - **Validation Character Calculation:**
   *   1. Replace the initial letter with a number:
   *      - `X` → `0`
   *      - `Y` → `1`
   *      - `Z` → `2`
   *   2. Concatenate the replaced number with the seven digits to form an eight-digit number.
   *   3. Apply the same validation algorithm as for NIF.
   *
   */
  private isValidationCharacterValidSpanish(taxNumber: string): boolean {
    const controlLetters = 'TRWAGMYFPDXBNJZSQVHLCKE';
    const nifRegex = /^[0-9]{8}[A-Z]$/;
    const nieRegex = /^[XYZ][0-9]{7}[A-Z]$/;

    let numberPart: string;
    let controlLetter: string;

    if (nifRegex.test(taxNumber)) {
      // NIF format (Spanish individuals)
      numberPart = taxNumber.substring(0, 8);
      controlLetter = taxNumber.substring(8, 9);
    } else if (nieRegex.test(taxNumber)) {
      // NIE format (Foreigners)
      const initialLetter = taxNumber.charAt(0);
      let prefixNumber = '';
      switch (initialLetter) {
        case 'X':
          prefixNumber = '0';
          break;
        case 'Y':
          prefixNumber = '1';
          break;
        case 'Z':
          prefixNumber = '2';
          break;
        default:
          return false;
      }
      numberPart = prefixNumber + taxNumber.substring(1, 8);
      controlLetter = taxNumber.substring(8, 9);
    } else {
      // Does not match NIF or NIE format
      return false;
    }

    // Convert the numeric part to a number and calculate the remainder
    const number = parseInt(numberPart, 10);
    const remainder = number % 23;

    const expectedControlLetter = controlLetters.charAt(remainder);
    return controlLetter === expectedControlLetter;
  }

  private validateTaxNumberPortuguese(taxNumber: string) {
    // Regular expression to match exactly 9 digits and ensure the first digit is not '0' or '4'
    const taxNumberRegex: RegExp = /^[12356789][0-9]{8}$/;
    return taxNumberRegex.test(taxNumber) && this.isChecksumValidPortuguese(taxNumber);
  }

  /**
   * Validates the checksum of a Portuguese Tax Number (NIF).
   *
   * @param taxNumber - A string representing the Portuguese Tax Number to validate. It must be exactly 9 digits long.
   * @returns A boolean indicating whether the checksum is valid (`true`) or not (`false`).
   *
   * @description
   * This function calculates the checksum digit of a Portuguese Tax Number (NIF) and compares it with the
   * provided checksum digit (the 9th digit of the NIF). The validation follows the algorithm specified
   * by the Portuguese Tax Authority:
   *
   * 1. **Weights Assignment:**
   *    - Assign weights to the first 8 digits of the NIF, starting from 9 and decreasing to 2.
   *      - Multiply the 1st digit by 9.
   *      - Multiply the 2nd digit by 8.
   *      - Continue this pattern until the 8th digit is multiplied by 2.
   *
   * 2. **Sum Calculation:**
   *    - Calculate the sum of the products obtained in step 1.
   *
   * 3. **Checksum Digit Calculation:**
   *    - Calculate the remainder of the sum when divided by 11 (`remainder = sum % 11`).
   *    - Determine the checksum digit:
   *      - If the remainder is 0 or 1, the checksum digit is 0.
   *      - If the remainder is greater than 1, the checksum digit is `11 - remainder`.
   *
   * 4. **Validation:**
   *    - Compare the calculated checksum digit with the 9th digit of the NIF.
   *    - If they match, the NIF has a valid checksum.
   *
   */
  private isChecksumValidPortuguese(taxNumber: string) {
    // Convert the NIF string into an array of numbers
    const digits = taxNumber.split('').map(Number);

    // Define the weights for each digit position
    const weights = [9, 8, 7, 6, 5, 4, 3, 2];

    // Calculate the sum of the products of the first 8 digits and their weights
    let sum = 0;
    for (let i = 0; i < 8; i++) {
      sum += digits[i] * weights[i];
    }

    // Calculate the remainder of the sum divided by 11
    const remainder = sum % 11;

    // Determine the checksum digit based on the remainder
    let checksum = 0;
    if (remainder !== 0 && remainder !== 1) {
      checksum = 11 - remainder;
    }

    // Check if the calculated checksum matches the 9th digit of the NIF
    return checksum === digits[8];
  }
}
